import { inspectionSheetKey } from '@/hooks/keyUtils'
import { useAuth0 } from '@auth0/auth0-react'
import { InspectionSheet, getInspectionSheets } from '@infraloop/services'
import { useQuery } from '@tanstack/react-query'

/**
 * Loads an inspection area's sheet — each area carries one, holding the inspection
 * metadata (construction properties, creator/witness names and dates) shown in the
 * area edit form. Gated on `enabled` so it only fetches while the form is open.
 *
 * @param projectId Project the area belongs to.
 * @param inspectionAreaId Area whose sheet to load (empty disables the query).
 * @param enabled Whether the edit form is open (drives lazy fetching).
 */
const useInspectionSheet = (projectId: string, inspectionAreaId: string, enabled: boolean) => {
  const { getAccessTokenSilently } = useAuth0()

  const { data: sheet, isLoading } = useQuery<InspectionSheet | null>({
    queryKey: inspectionSheetKey(projectId, inspectionAreaId),
    enabled: enabled && Boolean(projectId) && Boolean(inspectionAreaId),
    staleTime: 60 * 1000,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      const sheets = await getInspectionSheets(accessToken, projectId, inspectionAreaId)
      return sheets[0] ?? null
    },
  })

  return {
    sheet,
    isLoadingSheet: isLoading,
  }
}

export default useInspectionSheet
